import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export function useDashboardData(startDate: string, endDate: string) {
  const [data, setData] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      const [{ data: txs }, { data: accs }]: any = await Promise.all([
        supabase
          .from("transactions")
          .select("amount, type, date, account_id, category_id")
          .gte("date", startDate)
          .lte("date", endDate),
        supabase
          .from("accounts")
          .select("id, name, currency, initial_balance")
          .eq("status", true),
      ]);

      const groupedByCurrency: any = {};
      for (const acc of accs) {
        const currency = acc.currency;
        if (!groupedByCurrency[currency]) {
          groupedByCurrency[currency] = {
            totalIncome: 0,
            totalExpense: 0,
            netBalance: 0,
            totalBalance: 0,
            topCategories: {},
            topAccounts: [],
            trends: {},
          };
        }

        const accTxs = txs.filter((t: any) => t.account_id === acc.id);

        let income = 0;
        let expense = 0;
        const trendsMap: any = {};

        for (const t of accTxs) {
          const dateStr = t.date.split("T")[0];
          if (!trendsMap[dateStr]) {
            trendsMap[dateStr] = { date: dateStr, income: 0, expense: 0 };
          }

          if (t.type === "income") {
            income += t.amount;
            trendsMap[dateStr].income += t.amount;
          } else {
            expense += t.amount;
            trendsMap[dateStr].expense += t.amount;

            const key = t.category_id || "Sin categoría";
            groupedByCurrency[currency].topCategories[key] =
              (groupedByCurrency[currency].topCategories[key] || 0) + t.amount;
          }
        }
        const balance = accTxs.reduce(
          (sum: any, t: any) =>
            sum + (t.type === "income" ? t.amount : -t.amount),
          acc.initial_balance
        );

        groupedByCurrency[currency].totalIncome += income;
        groupedByCurrency[currency].totalExpense += expense;
        groupedByCurrency[currency].netBalance += income - expense;
        groupedByCurrency[currency].totalBalance += balance;

        for (const date in trendsMap) {
          if (!groupedByCurrency[currency].trends[date]) {
            groupedByCurrency[currency].trends[date] = {
              date,
              income: 0,
              expense: 0,
            };
          }
          groupedByCurrency[currency].trends[date].income +=
            trendsMap[date].income;
          groupedByCurrency[currency].trends[date].expense +=
            trendsMap[date].expense;
        }

        groupedByCurrency[currency].topAccounts.push({
          name: acc.name,
          count: accTxs.length,
        });
      }

      const finalData: any = {};

      for (const currency in groupedByCurrency) {
        const cData = groupedByCurrency[currency];
        const sortedTrends = Object.values(cData.trends).sort(
          (a: any, b: any) => a.date.localeCompare(b.date)
        );

        const topCategories = Object.entries(cData.topCategories)
          .map(([category, amount]) => ({ category, amount }))
          .sort((a: any, b: any) => b.amount - a.amount)
          .slice(0, 5);

        const topAccounts = cData.topAccounts
          .sort((a, b) => b.count - a.count)
          .slice(0, 10);

        finalData[currency] = {
          totalIncome: cData.totalIncome,
          totalExpense: cData.totalExpense,
          netBalance: cData.netBalance,
          totalBalance: cData.totalBalance,
          topCategories,
          trends: sortedTrends,
          topAccounts,
        };
      }

      setData(finalData);
    };

    fetchData();
  }, [startDate, endDate]);

  return data; // objeto con claves PEN, USD, etc.
}
